import { offBrandWords } from '../social/socialOptions'
import { emailImage, safeUrl } from './newsletterHtml'
import { fillTokens } from '../marketing/marketingRules'
import { isSoldOut } from '../../core/rules/stock'
import { today } from '../../core/format/dates'
import type { Check } from '../editing'
import type { NewsletterContext } from './newsletterHtml'
import type { NewsletterBlock, NewsletterBlockKind, NewsletterIssue, NewsletterStatus } from '../../core/types/newsletter'

/** Inbox-weergave: langer valt weg achter "…" op de telefoon. */
export const SUBJECT_MAX = 60
export const PREHEADER_MAX = 110
/** Meer producten maakt de mail een folder; vier is een rustig blok van twee rijen. */
export const PRODUCTS_MAX = 4

export const newsletterStatuses: { key: NewsletterStatus; label: string; text: string }[] = [
  { key: 'concept', label: 'Concept', text: 'Nog mee bezig' },
  { key: 'klaar', label: 'Klaar', text: 'Klaar om in Brevo te zetten' },
  { key: 'verstuurd', label: 'Verstuurd', text: 'Staat in de inbox van je lezers' },
]

export const statusLabel = (s: NewsletterStatus) => newsletterStatuses.find((x) => x.key === s)?.label ?? s

/** Wat er echt met de nieuwsbrief is gebeurd: de server (delivery) wint van wat Sarah zelf aanvinkte. */
export type IssueState = 'concept' | 'klaar' | 'ingepland' | 'verzonden'

export function issueState(issue: NewsletterIssue): IssueState {
  if (issue.delivery?.status === 'verzonden' || issue.status === 'verstuurd') return 'verzonden'
  if (issue.delivery?.status === 'ingepland') return 'ingepland'
  return issue.status === 'klaar' ? 'klaar' : 'concept'
}

export const issueStateLabel: Record<IssueState, string> = { concept: 'Concept', klaar: 'Klaar', ingepland: 'Ingepland', verzonden: 'Verzonden' }
export const issueStateTone: Record<IssueState, 'soft' | 'accent' | 'success'> = { concept: 'soft', klaar: 'accent', ingepland: 'accent', verzonden: 'success' }

/** Ingepland of verzonden: dan is de inhoud vast (de server weigert wijzigen met 409). */
export const isLocked = (issue: NewsletterIssue) => Boolean(issue.delivery)

export const blockKinds: { kind: NewsletterBlockKind; label: string; text: string }[] = [
  { kind: 'design', label: 'Ontwerp', text: 'Een ontwerp uit Ontwerpen als kop: kleur, kop, tekst en knop' },
  { kind: 'text', label: 'Tekst', text: 'Een persoonlijk stuk, met of zonder tussenkop' },
  { kind: 'products', label: 'Producten', text: `Tot ${PRODUCTS_MAX} producten met foto en prijs uit de webshop` },
  { kind: 'article', label: 'Journal', text: 'Een artikel met foto, korte tekst en “Lees verder”' },
  { kind: 'button', label: 'Knop', text: 'Eén duidelijke volgende stap' },
]

export const blockLabel = (k: NewsletterBlockKind) => blockKinds.find((b) => b.kind === k)?.label ?? k

export const emptyIssue = (patch: Partial<NewsletterIssue> = {}): NewsletterIssue => ({
  id: '',
  subject: '',
  preheader: '',
  status: 'concept',
  sendDate: '',
  notes: '',
  blocks: [
    { id: 'b1', kind: 'text', heading: '', body: 'Hoi,\n\n' },
    { id: 'b2', kind: 'text', heading: '', body: 'Liefs,\n{firstName}' },
  ],
  ...patch,
})

export function newBlock(kind: NewsletterBlockKind, id: string): NewsletterBlock {
  switch (kind) {
    case 'design':
      return { id, kind, designId: '' }
    case 'text':
      return { id, kind, heading: '', body: '' }
    case 'products':
      return { id, kind, heading: 'Uit de salon', productIds: [] }
    case 'article':
      return { id, kind, slug: '' }
    case 'button':
      return { id, kind, label: 'Bekijk de webshop', url: '' }
  }
}

/** Een id die nog niet in de mail voorkomt. */
export const nextBlockId = (blocks: NewsletterBlock[]) => {
  const taken = new Set(blocks.map((b) => b.id))
  let i = blocks.length + 1
  while (taken.has(`b${i}`)) i++
  return `b${i}`
}

/** Alle tekst die de lezer ziet, voor tokens- en toonchecks. */
function visibleText(issue: NewsletterIssue, ctx: NewsletterContext) {
  const parts = [issue.subject, issue.preheader]
  for (const b of issue.blocks) {
    if (b.kind === 'text') parts.push(b.heading, b.body)
    if (b.kind === 'products') parts.push(b.heading)
    if (b.kind === 'button') parts.push(b.label)
    if (b.kind === 'design') {
      const d = ctx.design(b.designId)
      if (d) parts.push(d.eyebrow, d.headline, d.accent, d.body, d.cta)
    }
  }
  return parts.map((p) => fillTokens(p, ctx.tokens)).join('\n')
}

/** Wat moet er nog gebeuren voordat deze nieuwsbrief naar Brevo kan? */
export function issueChecks(issue: NewsletterIssue, ctx: NewsletterContext): Check[] {
  const c: Check[] = []
  const subject = fillTokens(issue.subject, ctx.tokens).trim()

  if (!subject) c.push({ level: 'error', text: 'Nog geen onderwerp' })
  else if (subject.length > SUBJECT_MAX)
    c.push({ level: 'warning', text: `Het onderwerp is lang (${subject.length} tekens): op de telefoon valt alles na ± ${SUBJECT_MAX} weg` })
  if (!issue.preheader.trim()) c.push({ level: 'warning', text: 'Geen voorvertoningsregel: de inbox toont dan de eerste regel van de mail' })
  else if (issue.preheader.length > PREHEADER_MAX) c.push({ level: 'warning', text: `De voorvertoningsregel is lang: ± ${PREHEADER_MAX} tekens zijn zichtbaar` })

  if (issue.blocks.length === 0) c.push({ level: 'error', text: 'De mail is nog leeg: voeg een blok toe' })

  issue.blocks.forEach((b, i) => {
    const where = `Blok ${i + 1}`
    switch (b.kind) {
      case 'design': {
        const d = b.designId ? ctx.design(b.designId) : undefined
        if (!b.designId) c.push({ level: 'error', text: `${where}: kies een ontwerp` })
        else if (!d) c.push({ level: 'error', text: `${where}: het ontwerp bestaat niet meer` })
        else {
          if (d.image && !emailImage(d.image, ctx.siteUrl))
            c.push({ level: 'error', text: `${where}: de foto van “${d.name}” staat alleen in deze browser. Kies een foto uit de lijst in het ontwerp.` })
          if (d.cta && !safeUrl(d.qrUrl))
            c.push({ level: 'warning', text: `${where}: de knop van “${d.name}” heeft geen link (vul de QR-link in het ontwerp in), dus hij wordt weggelaten` })
        }
        break
      }
      case 'text':
        if (!b.heading.trim() && !b.body.trim()) c.push({ level: 'warning', text: `${where}: leeg tekstblok, wordt weggelaten` })
        break
      case 'products': {
        if (b.productIds.length === 0) c.push({ level: 'error', text: `${where}: kies minstens één product` })
        if (b.productIds.length > PRODUCTS_MAX) c.push({ level: 'warning', text: `${where}: meer dan ${PRODUCTS_MAX} producten leest als een folder` })
        b.productIds.forEach((id) => {
          const p = ctx.product(id)
          if (!p) c.push({ level: 'error', text: `${where}: een product bestaat niet meer` })
          else if (isSoldOut(p)) c.push({ level: 'warning', text: `${where}: ${p.name} is uitverkocht` })
        })
        break
      }
      case 'article':
        if (!b.slug) c.push({ level: 'error', text: `${where}: kies een artikel` })
        else if (!ctx.article(b.slug)) c.push({ level: 'error', text: `${where}: het artikel bestaat niet meer` })
        break
      case 'button':
        if (!b.label.trim()) c.push({ level: 'error', text: `${where}: de knop heeft geen tekst` })
        if (!safeUrl(b.url)) c.push({ level: 'error', text: `${where}: de knop heeft geen geldige link (begin met https://)` })
        break
    }
  })

  const text = visibleText(issue, ctx)
  const unknown = Array.from(new Set(text.match(/\{\w+\}/g) ?? []))
  if (unknown.length) c.push({ level: 'error', text: `Onbekende slimme woorden: ${unknown.join(', ')}` })

  const hasLink = issue.blocks.some((b) => b.kind === 'button' || b.kind === 'products' || b.kind === 'article')
  if (issue.blocks.length && !hasLink) c.push({ level: 'warning', text: 'Geen link of knop: geef de lezer één duidelijke volgende stap' })

  const lower = text.toLowerCase()
  offBrandWords.filter((w) => lower.includes(w)).forEach((w) => c.push({ level: 'warning', text: `“${w}” past niet bij hoe Beauty Bewust klinkt` }))

  if (issue.sendDate && issue.sendDate < today() && issueState(issue) !== 'verzonden' && issueState(issue) !== 'ingepland')
    c.push({ level: 'warning', text: 'De verzenddag is voorbij. Al verstuurd? Zet de status op Verstuurd.' })

  return c
}

/** Waar een ontwerp gebruikt wordt: zo weet Sarah wat er verandert als ze het aanpast of verwijdert. */
export const newslettersUsingDesign = (issues: NewsletterIssue[], designId: string) =>
  issues.filter((n) => n.blocks.some((b) => b.kind === 'design' && b.designId === designId))
